import { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Loader2, Plus, Trash2, Info } from "lucide-react";
import {
  useContracts,
  useCreateContract,
  useUpdateContract,
  useSwitchContractMode,
  useUploadContractSourcePdf,
  useDeleteContract,
  useContractFields,
  useSaveContractFields,
  type Contract,
  type ContractMode,
} from "@/hooks/useContracts";
import type { Lead } from "@/hooks/useLeads";
import type { Budget } from "@/hooks/useBudgets";
import { DocumentModeCards, type DocumentModeOption } from "../documents/DocumentModeCards";
import { DocumentRichTextAuthor } from "../documents/DocumentRichTextAuthor";
import { PdfUploadStep } from "../documents/PdfUploadStep";
import { PdfFieldEditor } from "../documents/PdfFieldEditor";
import { ClientSignersEditor } from "../documents/ClientSignersEditor";
import type { ClientSigner } from "../documents/types";
import { ContractSignatureDialog } from "./ContractSignatureDialog";
import { ContractSignatureTimeline } from "./ContractSignatureTimeline";
import { cn } from "@/lib/utils";
import { NEW_CONTRACT, resolveActiveContract } from "@/lib/contractWorkspace";

const statusConfig: Record<string, { label: string; pill: string; dot: string }> = {
  draft: { label: "Rascunho", pill: "bg-warn-soft text-warn", dot: "bg-warn" },
  sent: { label: "Aguardando assinatura", pill: "bg-accent text-primary", dot: "bg-primary" },
  partially_signed: {
    label: "Parcialmente assinado",
    pill: "bg-warn-soft text-warn",
    dot: "bg-warn",
  },
  signed: { label: "Assinado", pill: "bg-ok-soft text-ok", dot: "bg-ok" },
  rejected: { label: "Recusado", pill: "bg-bad-soft text-bad", dot: "bg-bad" },
};

const MODES: DocumentModeOption<ContractMode>[] = [
  {
    id: "richtext",
    title: "Editor de texto",
    desc: "Escreva o contrato aqui com variáveis do orçamento",
  },
  {
    id: "pdf_upload",
    title: "Enviar PDF pronto",
    desc: "Marque os campos de assinatura sobre o arquivo",
  },
];

function ContractPdfUpload({ contract, leadId }: { contract: Contract; leadId: string }) {
  const upload = useUploadContractSourcePdf();
  const updateContract = useUpdateContract(leadId);
  return (
    <PdfUploadStep
      uploading={upload.isPending}
      hint="Arraste o PDF do contrato aqui ou"
      onFile={async (file) => {
        const url = await upload.mutateAsync({ contractId: contract.id, file });
        updateContract.mutate({ id: contract.id, data: { source_pdf_url: url } });
      }}
    />
  );
}

function ContractFieldsEditor({ contract, lead }: { contract: Contract; lead: Lead }) {
  const { data: fields, isLoading } = useContractFields(contract.id);
  const save = useSaveContractFields(contract.id);
  const updateContract = useUpdateContract(lead.id);

  // Who the client-side signature fields (below) are assigned to. Configured
  // here, ahead of placing fields, so the field editor can offer a real slot
  // per signer instead of the lead's name pointing at the wrong order_index
  // (order_index 0 is always the pre-signed admin — send-contract-signature).
  const [clientSigners, setClientSigners] = useState<ClientSigner[]>(
    contract.pending_client_signers?.length
      ? contract.pending_client_signers
      : [{ name: lead.name, email: lead.email ?? "" }],
  );
  useEffect(() => {
    setClientSigners(
      contract.pending_client_signers?.length
        ? contract.pending_client_signers
        : [{ name: lead.name, email: lead.email ?? "" }],
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contract.id]);
  const signerLabels = [
    "Você (pré-assinado)",
    ...clientSigners.map((s, i) => s.name.trim() || `Cliente ${i + 1}`),
  ];

  return (
    <div className="space-y-3">
      <div className="space-y-2 rounded-xl border border-border bg-muted/40 p-3">
        <ClientSignersEditor value={clientSigners} onChange={setClientSigners} />
        <Button
          size="sm"
          variant="outline"
          disabled={updateContract.isPending}
          onClick={() =>
            updateContract.mutate({
              id: contract.id,
              data: { pending_client_signers: clientSigners },
            })
          }
        >
          Salvar signatários
        </Button>
      </div>
      <PdfFieldEditor
        sourcePdfUrl={contract.source_pdf_url!}
        signerLabels={signerLabels}
        fields={fields}
        fieldsLoading={isLoading}
        onSave={(f) => save.mutate(f)}
        saving={save.isPending}
      />
    </div>
  );
}

function ContractRichText({ contract, leadId }: { contract: Contract; leadId: string }) {
  const updateContract = useUpdateContract(leadId);
  return (
    <DocumentRichTextAuthor
      documentId={contract.id}
      content={contract.richtext_content}
      saving={updateContract.isPending}
      onSave={(md) => updateContract.mutate({ id: contract.id, data: { richtext_content: md } })}
      hint="Redija o contrato: cláusulas, título de seções (use os títulos ## do editor) e texto livre. As assinaturas são adicionadas automaticamente ao final do documento ao enviar para assinatura."
      placeholder="Redija as cláusulas do contrato..."
    />
  );
}

// Sentinel for "the operator clicked Novo": renders the empty mode picker
// instead of falling back to contracts[0] (which made the button a no-op).

export function ContractWorkspace({
  lead,
  activeBudget,
}: {
  lead: Lead;
  activeBudget: Budget | null;
}) {
  const { data: contracts = [], isLoading } = useContracts(lead.id);
  const createContract = useCreateContract();
  const updateContract = useUpdateContract(lead.id);
  const switchMode = useSwitchContractMode(lead.id);
  const deleteContract = useDeleteContract(lead.id);
  const [activeContractId, setActiveContractId] = useState<string | null>(null);
  const [signatureDialogOpen, setSignatureDialogOpen] = useState(false);
  const [pendingSwitch, setPendingSwitch] = useState<ContractMode | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [editing, setEditing] = useState(false);

  const activeContract = resolveActiveContract(contracts, activeContractId);

  const budgetSigned =
    !!activeBudget && (activeBudget.status === "signed" || activeBudget.status === "accepted");

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const handlePickMode = (mode: ContractMode) => {
    createContract.mutate(
      {
        lead_id: lead.id,
        budget_id: activeBudget?.id ?? null,
        mode,
        title: `Contrato – ${lead.name}`,
      },
      {
        onSuccess: (c) => {
          setActiveContractId(c.id);
          setEditing(true);
        },
      },
    );
  };

  const isReadyToSend =
    activeContract &&
    ((activeContract.mode === "richtext" && !!activeContract.richtext_content?.trim()) ||
      (activeContract.mode === "pdf_upload" && !!activeContract.source_pdf_url));

  const status = activeContract
    ? (statusConfig[activeContract.status] ?? statusConfig.draft)
    : null;
  const isDraft = activeContract?.status === "draft";
  const pdfUrl =
    activeContract?.signed_pdf_url ??
    activeContract?.generated_pdf_url ??
    activeContract?.source_pdf_url ??
    null;
  const showEditor = activeContract && isDraft && (editing || !isReadyToSend);

  return (
    <section className="flex flex-col gap-[18px] rounded-2xl border border-border bg-card px-6 py-[22px] shadow-card">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-[200px] flex-1 items-center gap-2">
          {activeContract && isDraft ? (
            <Input
              key={activeContract.id}
              defaultValue={activeContract.title}
              className="h-8 max-w-sm border-transparent bg-transparent px-1 font-display text-[15px] font-bold hover:border-border focus-visible:border-border"
              onBlur={(e) => {
                const v = e.target.value.trim();
                if (v && v !== activeContract.title)
                  updateContract.mutate({ id: activeContract.id, data: { title: v } });
              }}
            />
          ) : (
            <h2 className="font-display text-[15px] font-bold">
              {activeContract ? activeContract.title : `Contrato — ${lead.name}`}
            </h2>
          )}
        </div>
        <div className="flex items-center gap-2">
          {status && (
            <span
              className={cn(
                "inline-flex h-[23px] items-center rounded-[7px] px-2.5 text-[11px] font-bold",
                status.pill,
              )}
            >
              {status.label}
            </span>
          )}
          {activeContract && isDraft && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs text-destructive hover:text-destructive"
              onClick={() => setConfirmDelete(true)}
              title="Excluir rascunho"
            >
              <Trash2 className="mr-1 h-3 w-3" /> Excluir rascunho
            </Button>
          )}
          {activeContract && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs"
              onClick={() => {
                setActiveContractId(NEW_CONTRACT);
                setEditing(false);
              }}
              title="Novo contrato"
            >
              <Plus className="mr-1 h-3 w-3" /> Novo
            </Button>
          )}
        </div>
      </div>

      {!activeContract && !budgetSigned && (
        <div className="flex items-start gap-2 rounded-xl border border-border bg-muted/60 px-3.5 py-2.5 text-[12.5px] text-muted-foreground">
          <Info className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            {activeBudget
              ? "A proposta ainda não foi assinada. Recomendado: envie a proposta para assinatura antes do contrato — o contrato será vinculado a ela."
              : "Nenhum orçamento selecionado. O contrato será criado sem vínculo com uma proposta; crie a proposta primeiro para liberar os pagamentos ao final."}
          </span>
        </div>
      )}

      {contracts.length > 1 && (
        <ToggleGroup
          type="single"
          value={activeContract?.id ?? ""}
          onValueChange={(v) => {
            if (v) setActiveContractId(v);
          }}
          className="flex-wrap justify-start gap-2"
        >
          {contracts.map((c) => {
            const cfg = statusConfig[c.status] ?? statusConfig.draft;
            return (
              <ToggleGroupItem
                key={c.id}
                value={c.id}
                className="h-auto gap-1.5 rounded-full border border-border px-2.5 py-1 text-xs font-medium hover:border-primary data-[state=on]:border-primary data-[state=on]:bg-primary data-[state=on]:text-primary-foreground"
              >
                <span className={cn("h-1.5 w-1.5 rounded-full", cfg.dot)} />
                {c.title}
              </ToggleGroupItem>
            );
          })}
        </ToggleGroup>
      )}

      {!activeContract && (
        <p className="-mb-2 text-[12.5px] text-muted-foreground">
          Como você quer montar o contrato?
        </p>
      )}
      <DocumentModeCards
        modes={MODES}
        selected={activeContract?.mode ?? null}
        disabled={
          createContract.isPending || switchMode.isPending || (!!activeContract && !isDraft)
        }
        onPick={(mode) => {
          if (!activeContract) return handlePickMode(mode);
          if (mode !== activeContract.mode && isDraft) setPendingSwitch(mode);
        }}
      />

      {activeContract && (
        <>
          {showEditor ? (
            activeContract.mode === "richtext" ? (
              <ContractRichText contract={activeContract} leadId={lead.id} />
            ) : !activeContract.source_pdf_url ? (
              <ContractPdfUpload contract={activeContract} leadId={lead.id} />
            ) : (
              <ContractFieldsEditor contract={activeContract} lead={lead} />
            )
          ) : activeContract.mode === "richtext" && activeContract.richtext_content ? (
            <div className="prose prose-sm max-h-[320px] max-w-none overflow-y-auto rounded-[13px] border border-border bg-muted px-5 py-[18px] text-[13px] leading-[1.75] text-muted-foreground [&_h1]:text-[13px] [&_h1]:font-bold [&_h1]:text-foreground [&_h2]:text-[13px] [&_h2]:font-bold [&_h2]:text-foreground [&_h3]:text-[13px] [&_h3]:font-bold [&_h3]:text-foreground [&_strong]:text-foreground">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {activeContract.richtext_content}
              </ReactMarkdown>
            </div>
          ) : activeContract.source_pdf_url ? (
            <div className="overflow-hidden rounded-[13px] border border-border bg-muted">
              <iframe
                src={pdfUrl ?? activeContract.source_pdf_url}
                title="Contrato"
                className="h-[320px] w-full"
              />
            </div>
          ) : null}

          {isDraft && isReadyToSend && !editing && (
            <div className="-mt-2 flex justify-end">
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-xs"
                onClick={() => setEditing(true)}
              >
                Editar contrato
              </Button>
            </div>
          )}

          {activeContract.status !== "draft" && (
            <ContractSignatureTimeline contract={activeContract} />
          )}

          <div className="flex flex-wrap justify-end gap-[9px]">
            {pdfUrl && (
              <Button
                asChild
                variant="outline"
                className="h-[38px] rounded-[10px] px-[15px] text-[12.5px] font-semibold"
              >
                <a href={pdfUrl} target="_blank" rel="noopener noreferrer" download>
                  Baixar PDF
                </a>
              </Button>
            )}
            {isDraft && (
              <Button
                className="h-[38px] rounded-[10px] px-[17px] text-[12.5px] font-semibold"
                onClick={() => setSignatureDialogOpen(true)}
                disabled={!isReadyToSend}
              >
                Enviar para assinatura
              </Button>
            )}
            {(activeContract.status === "sent" ||
              activeContract.status === "partially_signed" ||
              activeContract.status === "rejected") && (
              <Button
                className="h-[38px] rounded-[10px] px-[17px] text-[12.5px] font-semibold"
                onClick={() => setSignatureDialogOpen(true)}
              >
                Reenviar para assinatura
              </Button>
            )}
          </div>

          <ContractSignatureDialog
            contract={activeContract}
            lead={lead}
            open={signatureDialogOpen}
            onOpenChange={setSignatureDialogOpen}
          />

          <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
            <AlertDialogContent className="max-w-[430px] rounded-[18px]">
              <AlertDialogHeader>
                <AlertDialogTitle className="text-[17px] tracking-[-0.02em]">
                  Excluir rascunho do contrato?
                </AlertDialogTitle>
                <AlertDialogDescription className="text-[13px] leading-[1.6]">
                  "{activeContract.title}" será removido junto com o texto/PDF e os campos
                  posicionados. Esta ação não pode ser desfeita.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel className="h-[38px] rounded-[10px] text-[12.5px] font-semibold">
                  Cancelar
                </AlertDialogCancel>
                <AlertDialogAction
                  className="h-[38px] rounded-[10px] bg-bad text-[12.5px] font-semibold text-bad-foreground hover:bg-bad/90"
                  onClick={() => {
                    deleteContract.mutate(activeContract.id, {
                      onSuccess: () => setActiveContractId(null),
                    });
                    setConfirmDelete(false);
                  }}
                >
                  Excluir
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          <AlertDialog
            open={!!pendingSwitch}
            onOpenChange={(o) => {
              if (!o) setPendingSwitch(null);
            }}
          >
            <AlertDialogContent className="max-w-[430px] rounded-[18px]">
              <AlertDialogHeader>
                <AlertDialogTitle className="text-[17px] tracking-[-0.02em]">
                  Trocar tipo de contrato?
                </AlertDialogTitle>
                <AlertDialogDescription className="text-[13px] leading-[1.6]">
                  {activeContract.mode === "richtext"
                    ? "O texto redigido até agora será descartado. Você poderá subir um PDF em seguida."
                    : "O PDF enviado e os campos posicionados serão descartados. Você poderá redigir o contrato no editor em seguida."}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel className="h-[38px] rounded-[10px] text-[12.5px] font-semibold">
                  Cancelar
                </AlertDialogCancel>
                <AlertDialogAction
                  className="h-[38px] rounded-[10px] text-[12.5px] font-semibold"
                  onClick={() => {
                    if (pendingSwitch)
                      switchMode.mutate({ id: activeContract.id, mode: pendingSwitch });
                    setPendingSwitch(null);
                    setEditing(true);
                  }}
                >
                  Trocar
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </>
      )}
    </section>
  );
}
